import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaHeart,
  FaShower,
  FaUtensils,
  FaHandHoldingHeart,
  FaChild,
  FaUserFriends,
  FaPhone,
  FaMapMarkerAlt,
  FaClock,
  FaNewspaper
} from 'react-icons/fa';
import './Home.css';

const LANGUAGES = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'uk', name: 'Українська', flag: '🇺🇦' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'sw', name: 'Kiswahili', flag: '🇰🇪' },
  { code: 'wo', name: 'Wolof', flag: '🇸🇳' },
  { code: 'am', name: 'አማርኛ', flag: '🇪🇹' },
];

function Home() {
  const { t, i18n } = useTranslation();
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <span className="eyebrow">{t('home.since2003')}</span>
          <h1>{t('home.heroTitle')}</h1>
          <p className="lead">
            {t('home.heroDescription')}
          </p>
          <div className="actions">
            <Link to="/renseignements-pratiques" className="btn primary">
              {t('home.contactUs')}
            </Link>
            <Link to="/actualites" className="btn outline">
              <FaNewspaper /> {t('home.ourNews')}
            </Link>
          </div>
        </div>
      </section>

      {/* Languages showcase */}
      <section className="lang-showcase">
        <div className="lang-showcase-inner">
          <div className="lang-showcase-text">
            <span className="lang-showcase-badge">🌍</span>
            <p>
              Ce site est disponible en <strong>14 langues</strong> · This site is available in <strong>14 languages</strong> · هذا الموقع متاح بـ <strong>١٤ لغة</strong> · Цей сайт доступний <strong>14 мовами</strong>
            </p>
          </div>
          <div className="lang-showcase-flags">
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                className={`lang-showcase-btn ${i18n.language === lang.code ? 'active' : ''}`}
                onClick={() => i18n.changeLanguage(lang.code)}
                title={lang.name}
                aria-label={lang.name}
              >
                <span className="lang-showcase-flag">{lang.flag}</span>
                <span className="lang-showcase-name">{lang.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Why We Exist Section */}
      <section className="why-section">
        <div className="container">
          <div className="why-content">
            <div className="why-text">
              <span className="badge">{t('home.mission')}</span>
              <h2>{t('home.whyTitle')}</h2>
              <p>
                {t('home.whyDescription')}
              </p>
              <div className="why-image">
                <img 
                  src="/uploads/2024/06/Noel-RIVE-3-700x844.jpeg" 
                  alt="Femmes et enfants accueillis à RIVE" 
                  className="why-image-content"
                />
              </div>
              <blockquote className="mission-quote">
                <p>
                  <strong dangerouslySetInnerHTML={{ __html: t('home.missionQuote') }} />
                </p>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="container">
          <div className="section-header">
            <h2>{t('home.servicesTitle')}</h2>
            <p>{t('home.servicesSubtitle')}</p>
          </div>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">
                <FaHeart />
              </div>
              <h3>{t('home.service1Title')}</h3>
              <p>
                {t('home.service1Desc')}
              </p>
            </div>
            <div className="service-card">
              <div className="service-icon">
                <FaUserFriends />
              </div>
              <h3>{t('home.service2Title')}</h3>
              <p>
                {t('home.service2Desc')}
              </p>
            </div>
            <div className="service-card">
              <div className="service-icon">
                <FaHandHoldingHeart />
              </div>
              <h3>{t('home.service3Title')}</h3>
              <p>
                {t('home.service3Desc')}
              </p>
            </div>
            <div className="service-card">
              <div className="service-icon">
                <FaUtensils />
              </div>
              <h3>{t('home.service4Title')}</h3>
              <p>
                {t('home.service4Desc')}
              </p>
            </div>
            <div className="service-card">
              <div className="service-icon">
                <FaShower />
              </div>
              <h3>{t('home.service5Title')}</h3>
              <p>
                {t('home.service5Desc')}
              </p>
            </div>
            <div className="service-card">
              <div className="service-icon">
                <FaChild />
              </div>
              <h3>{t('home.service6Title')}</h3>
              <p>
                {t('home.service6Desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Donation CTA */}
      <section className="donation-cta">
        <div className="container">
          <div className="cta-content">
            <div className="cta-text">
              <h2>{t('home.donationTitle')}</h2>
              <p>
                {t('home.donationDesc')}
              </p>
              <p className="cta-note">
                {t('home.donationNote')}
              </p>
              <a 
                href="https://www.helloasso.com/associations/rive/formulaires/1" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn primary large"
              >
                {t('home.donateButton')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="contact-section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-card">
              <div className="contact-icon">
                <FaMapMarkerAlt />
              </div>
              <h3>{t('home.address')}</h3>
              <p dangerouslySetInnerHTML={{ __html: t('home.addressValue') }} />
            </div>
            <div className="contact-card">
              <div className="contact-icon">
                <FaClock />
              </div>
              <h3>{t('home.hours')}</h3>
              <p dangerouslySetInnerHTML={{ __html: t('home.hoursValue') }} />
            </div>
            <div className="contact-card">
              <div className="contact-icon">
                <FaPhone />
              </div>
              <h3>{t('home.phone')}</h3>
              <p><a href="tel:+33130213450">01 30 21 34 50</a></p>
            </div>
            <div className="contact-card">
              <div className="contact-icon">
                <FaHandHoldingHeart />
              </div>
              <h3>{t('home.contact')}</h3>
              <p>
                <Link to="/renseignements-pratiques" className="contact-link">
                  {t('home.contactForm')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
